/**
 * Looks up a translation string by key from window.theme.strings.
 * Falls back to the provided fallback or the key itself if not found.
 *
 * @param {string} key - Dot-separated translation key
 * @param {string} [fallback] - Optional fallback string
 * @returns {string} - The translated string or fallback
 */
function t(key, fallback) {
  // Default fallback if none provided
  const defaultText = fallback || key;

  // Check if theme and strings exist first
  if (!window.theme || !window.theme.strings) {
    return defaultText;
  }

  // Try to find the translation by navigating the nested structure
  try {
    const parts = key.split('.');
    // Handle the new structure
    let result = window.theme.strings;

    // Navigate through each part of the key
    for (const part of parts) {
      if (result[part] === undefined) return defaultText;
      result = result[part];
    }

    return result || defaultText;
  } catch (e) {
    return defaultText;
  }
}

// ---------------
// PDP Custom Dropdown Code
// ---------------
document.addEventListener('DOMContentLoaded', () => {
  // Get current market from <html data-market="...">
  const market = document.documentElement.getAttribute('data-market');
  
  // Translation helper function - improved with fallbacks

  // Logging flag and helper
  const ENABLE_LOGS = true; // Set to true to enable logs
  /**
   * Logs debug information to the console with the [PEL] prefix if ENABLE_LOGS is true.
   * @param {...any} args
   */
  function log(...args) {
    if (ENABLE_LOGS) console.log('[PEL]', ...args);
  }

  log('DOMContentLoaded fired');
  log('Current market:', market);

  // ---------------
  // Price State Management
  // ---------------
  // Centralized price state management
  window.priceState = {
    variantPrice: 0,
    variantComparePrice: 0,
    framePrice: 0,
    frameComparePrice: 0,
    /**
     * Computes the total price by summing variant and frame prices.
     * @returns {number}
     */
    get totalPrice() {
      return parseFloat(this.variantPrice) + parseFloat(this.framePrice || 0);
    },
    /**
     * Computes the total compare-at price (if available) for variant and frame.
     * @returns {number|null}
     */
    get totalComparePrice() {
      return (
        this.variantComparePrice &&
        parseFloat(this.variantComparePrice) + parseFloat(this.frameComparePrice || 0)
      );
    },
    /**
     * Triggers a UI update for prices using the current state.
     * Side effect: calls global window.updatePrices.
     */
    updatePrices() {
      window.updatePrices(
        this.variantPrice,
        this.framePrice,
        this.variantComparePrice,
        this.frameComparePrice
      );
    },
  };

  // ---------------
  // Frame Selection Management
  // ---------------
  // Enhanced frame selection object with methods to update prices
  window.frameSelection = {
    isFrameSelected: false,
    framePrice: 0,
    frameHandle: null,
    frameTitle: null,
    frameVariant: null,
    frameId: null,
    frameComparePrice: 0,

    // Add methods to update the object and trigger price updates

    /**
     * Selects a frame and updates all related state.
     * @param {Object} data - Frame data (price, comparePrice, handle, title, variant, id)
     * @returns {this}
     * Side effects: Updates global price state and triggers price update.
     */
    setFrame(data) {
      // Ensure data is valid
      if (!data || typeof data !== 'object') {
        console.error('[PEL] Invalid frame data provided to setFrame():', data);
        return;
      }

      // Update all properties
      this.isFrameSelected = true;
      this.framePrice = parseFloat(data.price || 0);
      this.frameComparePrice = parseFloat(data.comparePrice || 0);
      this.frameHandle = data.handle || null;
      this.frameTitle = data.title || null;
      this.frameVariant = data.variant || null;
      this.frameId = data.id || null;

      // Update global price state using sync function if available
      if (typeof window.syncFrameState === 'function') {
        window.syncFrameState();
      } else {
        // Fallback direct update
        window.priceState.framePrice = this.framePrice;
        window.priceState.frameComparePrice = this.frameComparePrice;
      }

      // Log current state
      if (ENABLE_LOGS) {
        console.log('[PEL] Frame selected:', {
          price: this.framePrice,
          comparePrice: this.frameComparePrice,
          title: this.frameTitle,
          priceState: window.priceState,
        });
      }

      // Trigger price update
      window.priceState.updatePrices();

      return this; // Enable chaining
    },

    /**
     * Clears the current frame selection and resets related state.
     * Side effects: Updates global price state and triggers price update.
     */
    clearFrame() {
      // Reset all properties
      this.isFrameSelected = false;
      this.framePrice = 0;
      this.frameComparePrice = 0;
      this.frameHandle = null;
      this.frameTitle = null;
      this.frameVariant = null;
      this.frameId = null;

      // Update global price state using sync function if available
      if (typeof window.syncFrameState === 'function') {
        window.syncFrameState();
      } else {
        // Fallback direct update
        window.priceState.framePrice = 0;
        window.priceState.frameComparePrice = 0;
      }

      // Reset delivery display to normal when frame is cleared
      const estimateDelivery = document.querySelector('.estimate-shipping .normal-estimate');
      const extendedDelivery = document.querySelector('.estimate-shipping .extended');
      if (estimateDelivery && extendedDelivery) {
        estimateDelivery.style.display = 'block';
        extendedDelivery.style.display = 'none';
      }

      // Log current state
      if (ENABLE_LOGS) {
        console.log('[PEL] Frame cleared, price state:', window.priceState);
      }

      // Trigger price update
      window.priceState.updatePrices();

      return this; // Enable chaining
    },
  };

  // ---------------
  // State Synchronization
  // ---------------
  // Add synchronization method to ensure frameSelection and priceState stay aligned
  /**
   * Synchronizes frameSelection and priceState, ensuring both are aligned with the current selection.
   * Side effects: Mutates window.priceState.
   */
  function syncFrameState() {
    // If a frame is selected, ensure priceState has its values
    if (window.frameSelection.isFrameSelected) {
      window.priceState.framePrice = window.frameSelection.framePrice;
      window.priceState.frameComparePrice = window.frameSelection.frameComparePrice;
    } else {
      // No frame selected, ensure frame prices are 0
      window.priceState.framePrice = 0;
      window.priceState.frameComparePrice = 0;
    }
    return window.priceState;
  }

  // Track the latest variant ID to handle rapid switching
  window.latestVariantId = null;

  // Listen for variant changes from template-product.js
  document.addEventListener('variant:changed', event => {
    const variantId = event.detail.variantId;

    log('variant:changed event received for variant ID', variantId, {
      variantPrice: window.priceState.variantPrice,
      framePrice: window.priceState.framePrice,
      frameSelected: window.frameSelection.isFrameSelected,
      totalPrice: window.priceState.totalPrice,
    });
    
    // IMPORTANT CHANGE: Always accept variant:changed events
    // Instead of skipping events, always use the most recent one
    // This ensures synchronization between tracking mechanisms
    
    // Update the latest variant ID
    window.latestVariantId = variantId;

    // Update the price state with new variant info
    window.priceState.variantPrice = event.detail.price;
    window.priceState.variantComparePrice = event.detail.comparePrice;

    // Explicitly sync frame data from frameSelection to priceState
    syncFrameState();

    // Update frame dropdown display if frame is selected (this may update frame prices)
    if (window.frameSelection.isFrameSelected && event.detail.variantTitle) {
      updateFrameDropdownDisplay(event.detail.variantTitle);
    } else {
      // If no frame selected, still update dropdown options for correct prices/availability
      if (event.detail.variantTitle) {
        updateAllDropdownOptions(event.detail.variantTitle);
      }
      // Then trigger price update
      window.priceState.updatePrices();
    }

    // Debug logging to track state
    log('Processing variant:changed event for variant ID', variantId, {
      variantPrice: window.priceState.variantPrice,
      framePrice: window.priceState.framePrice,
      frameSelected: window.frameSelection.isFrameSelected,
      totalPrice: window.priceState.totalPrice,
    });
  });

  // Make syncFrameState available globally for debugging
  window.syncFrameState = syncFrameState;

  // ---------------
  // Debug Helpers
  // ---------------
  // Add debug helper function
  /**
   * Debug helper to log the current price and frame selection state to the console.
   */
  window.debugPriceState = function () {
    console.group('Price State Debug');
    console.log('frameSelection:', {
      ...window.frameSelection,
    });
    console.log('priceState:', {
      ...window.priceState,
      framePrice: window.priceState.framePrice,
      variantComparePrice: window.priceState.variantComparePrice,
      frameComparePrice: window.priceState.frameComparePrice,
      totalPrice: window.priceState.totalPrice,
      totalComparePrice: window.priceState.totalComparePrice,
    });

    // Check for synchronization issues
    const issuesFound = [];
    if (window.frameSelection.isFrameSelected && window.priceState.framePrice === 0) {
      issuesFound.push('Frame is selected but priceState.framePrice is 0');
    }
    if (
      window.frameSelection.isFrameSelected &&
      window.priceState.framePrice !== window.frameSelection.framePrice
    ) {
      issuesFound.push("Frame prices don't match between objects");
    }
    if (!window.frameSelection.isFrameSelected && window.priceState.framePrice > 0) {
      issuesFound.push('No frame selected but priceState.framePrice > 0');
    }

    if (issuesFound.length > 0) {
      console.warn('Synchronization issues found:', issuesFound);
      console.log('Attempting to fix...');
      window.syncFrameState();
      console.log('After sync:', {
        framePrice: window.priceState.framePrice,
        totalPrice: window.priceState.totalPrice,
      });
    } else {
      console.log('No synchronization issues found');
    }

    console.groupEnd();
  };

  // Run initial sync to ensure state is consistent on load
  syncFrameState();

  // ---------------
  // Price Update Logic
  // ---------------
  // Universal price update function with debounce mechanism
  let priceUpdateTimer = null;
  /**
   * Updates all price displays throughout the product page.
   * Applies debouncing to avoid redundant updates from rapid changes.
   *
   * @param {Number} price - The variant price
   * @param {Number} framePrice - The optional frame price
   * @param {Number|null} comparePrice - The variant compare-at price (optional)
   * @param {Number} frameComparePrice - The frame compare price (optional)
   * Side effects: Updates DOM elements showing price, compare price, and custom button price.
   */
  function updatePrices(price, framePrice = 0, comparePrice = null, frameComparePrice = 0) {
    log('updatePrices called with', { price, framePrice, comparePrice, frameComparePrice });

    // Clear any pending updates
    if (priceUpdateTimer) {
      clearTimeout(priceUpdateTimer);
    }

    // Create a short delay to ensure we get the most recent update when switching rapidly
    priceUpdateTimer = setTimeout(() => {
      // Use parameters if provided, otherwise fall back to the global state
      // This helps with backward compatibility where old code may call this directly
      const effectivePrice = price || window.priceState.variantPrice;
      const effectiveFramePrice =
        framePrice !== undefined ? framePrice : window.priceState.framePrice;
      const effectiveComparePrice = comparePrice || window.priceState.variantComparePrice;
      const effectiveFrameComparePrice =
        frameComparePrice !== undefined ? frameComparePrice : window.priceState.frameComparePrice;

      log('Effective prices:', {
        effectivePrice,
        effectiveFramePrice,
        effectiveComparePrice,
        effectiveFrameComparePrice,
      });

      const totalPrice = effectiveFramePrice
        ? parseFloat(effectivePrice) + parseFloat(effectiveFramePrice)
        : parseFloat(effectivePrice);
      const totalComparePrice =
        effectiveComparePrice &&
        parseFloat(effectiveComparePrice) + parseFloat(effectiveFrameComparePrice || 0);

      // Main add to cart button price
      if (mainAtc) {
        const mainPriceContainer = mainAtc.querySelector('.total-price');
        if (mainPriceContainer) {
          mainPriceContainer.textContent = Woolman.Utils.formatMoney(
            totalPrice,
            theme.settings.moneyFormat
          );
          log('Updated mainAtc price to', totalPrice);
        }

        // Main compare price (if exists)
        const mainComparePriceContainer = mainAtc.querySelector('.total-compare-price');
        if (mainComparePriceContainer && totalComparePrice) {
          mainComparePriceContainer.textContent = Woolman.Utils.formatMoney(
            totalComparePrice,
            theme.settings.moneyFormat
          );
          mainComparePriceContainer.style.display = 'inline-block';
          log('Updated mainAtc compare price to', totalComparePrice);
        } else if (mainComparePriceContainer) {
          mainComparePriceContainer.style.display = 'none';
        }
      }

      // Main product price - use totalPrice for correct calculation
      if (mainPrdPrice) {
        // Make sure we're using the total price (variant + frame)
        mainPrdPrice.textContent = Woolman.Utils.formatMoney(
          totalPrice,
          theme.settings.moneyFormat
        );
        log('Updated mainPrdPrice to', totalPrice, '(variant:', price, '+ frame:', framePrice, ')');

        // Main product compare price
        const mainPrdComparePrice = document.querySelector('.product-main .price__compare .price-item');
        if (mainPrdComparePrice && totalComparePrice) {
          mainPrdComparePrice.textContent = Woolman.Utils.formatMoney(
            totalComparePrice,
            theme.settings.moneyFormat
          );
          mainPrdComparePrice.closest('.price__compare').style.display = 'block';
          log('Updated mainPrdComparePrice to', totalComparePrice);
        } else if (mainPrdComparePrice) {
          mainPrdComparePrice.closest('.price__compare').style.display = 'none';
        }

        // Additional compare price elements (for sale items)
        const comparePriceElements = document.querySelectorAll('.product-main .price-item--regular');
        comparePriceElements.forEach(element => {
          if (totalComparePrice) {
            element.textContent = Woolman.Utils.formatMoney(
              totalComparePrice,
              theme.settings.moneyFormat
            );
            element.style.display = 'inline';
            log('Updated compare price element to', totalComparePrice);
          }
        });
      }

      // Sale price - update if it exists
      const salePriceElement = document.querySelector('.product-main .price-item--sale');
      if (salePriceElement) {
        salePriceElement.textContent = Woolman.Utils.formatMoney(
          totalPrice,
          theme.settings.moneyFormat
        );
        log('Updated sale price to', totalPrice);
      }

      // Regular price - update if it exists (for products on sale)
      const regularPriceElement = document.querySelector('.product-main .price-item--regular');
      if (regularPriceElement && totalComparePrice) {
        regularPriceElement.textContent = Woolman.Utils.formatMoney(
          totalComparePrice,
          theme.settings.moneyFormat
        );
        log('Updated regular price to', totalComparePrice);
      }

      // Buy bar price
      if (buyBarPrice) {
        buyBarPrice.textContent = Woolman.Utils.formatMoney(totalPrice, theme.settings.moneyFormat);
        log('Updated buyBarPrice to', totalPrice);

        // Buy bar compare price
        const buyBarComparePrice = document.querySelector('.the-buy-bar .compare-price');
        if (buyBarComparePrice && totalComparePrice) {
          buyBarComparePrice.textContent = Woolman.Utils.formatMoney(
            totalComparePrice,
            theme.settings.moneyFormat
          );
          buyBarComparePrice.style.display = 'inline-block';
          log('Updated buyBarComparePrice to', totalComparePrice);
        } else if (buyBarComparePrice) {
          buyBarComparePrice.style.display = 'none';
        }
      }

      // Size price tag (if it exists)
      const sizePrice = document.querySelector('.size-total-price');
      if (sizePrice) {
        const variantPriceOnly = price || window.priceState.variantPrice || 0;
        sizePrice.innerHTML = ` + ${Woolman.Utils.formatMoney(
          variantPriceOnly,
          theme.settings.moneyFormat
        )}`;
        log('Updated sizePrice to', variantPriceOnly);
      }

      // Custom button price (for frames)
      if (customBtn) {
        const customPriceContainer = customBtn.querySelector('.total-price');
        if (customPriceContainer) {
          customPriceContainer.textContent = Woolman.Utils.formatMoney(
            totalPrice,
            theme.settings.moneyFormat
          );
          log('Updated custom button price to', totalPrice);
        }

        // Custom button compare price
        if (comparePriceContainer && totalComparePrice) {
          comparePriceContainer.textContent = Woolman.Utils.formatMoney(
            totalComparePrice,
            theme.settings.moneyFormat
          );
          comparePriceContainer.style.display = 'inline-block';
          log('Updated custom button compare price to', totalComparePrice);
        } else if (comparePriceContainer) {
          comparePriceContainer.style.display = 'none';
        }
      }
    }, 10); // Small timeout to ensure we process the latest change
  }
  window.updatePrices = updatePrices;

  const dropdownContainer = document.querySelector('.dropdown-container');
  const selectedOption = document.querySelector('.selected-option');
  const availableText = document.querySelector('.available-text');
  const dropdownList = document.querySelector('.dropdwon-list');
  const dropdownOptions = document.querySelectorAll('.dropdown-option');
  const customBtn = document.querySelector('.product-form__buttons .custom-btn');
  const mainPrdPrice = document.querySelector('.price__regular .price-item');
  const buyBarPrice = document.querySelector('.the-buy-bar .price-item');
  const variantSelect = document.querySelector('variant-selects select');
  const estimateDelivery = document.querySelector('.estimate-shipping .normal-estimate');
  const extendedDelivery = document.querySelector('.estimate-shipping .extended');
  let priceContainer, comparePriceContainer;
  const mainAtc = document.querySelector('.product-form__submit');

  // Parse frame-data JSON
  let frameData = {};
  const frameDataScript = document.getElementById('frame-data');
  if (frameDataScript) {
    try {
      frameData = JSON.parse(frameDataScript.textContent);
      window.frameData = frameData;
      log('Frame data parsed successfully', Object.keys(frameData));
    } catch (e) {
      console.error('[PEL] Failed to parse frame-data', e);
    }
  }

  // Simple API cache
  const apiCache = {};
  /**
   * Fetches a URL and caches the response for subsequent calls.
   * Used to avoid redundant API requests for the same resource.
   *
   * @param {string} url
   * @returns {Promise<Response>}
   */
  async function cachedFetch(url) {
    if (apiCache[url]) {
      return Promise.resolve(apiCache[url]);
    }
    const response = await fetch(url);
    const data = await response.json();
    apiCache[url] = data;
    return data;
  }

  // ---------------
  // Helper Functions
  // ---------------
  /**
   * Gets the currently selected variant value from either button or dropdown picker
   * @returns {string|null} The selected variant value or null if none found
   */
  function getCurrentVariantValue() {
    // Try button picker first
    const radioInput = document.querySelector(".product-variant-options input[type='radio']:checked");
    if (radioInput) {
      return radioInput.value;
    }
    
    // Try dropdown picker
    const selectInput = document.querySelector('variant-selects select');
    if (selectInput) {
      return selectInput.value;
    }
    
    return null;
  }

  /**
   * Gets the product handle from either button or dropdown picker
   * @returns {string|null} The product handle or null if none found
   */
  function getCurrentProductHandle() {
    const variantRadios = document.querySelector('variant-radios');
    const variantSelects = document.querySelector('variant-selects');
    
    return variantRadios 
      ? variantRadios.getAttribute('data-handle')
      : variantSelects?.getAttribute('data-handle');
  }

  // ---------------
  // Frame Dropdown Display Update
  // ---------------
  /**
   * Resets frame selection to "No Frame" state
   */
  function resetToNoFrame() {
    const selectedOption = document.querySelector('.selected-option');
    const customBtn = document.querySelector('.custom-btn');
    const mainAtc = document.querySelector('.product-form__submit');
    const estimateDelivery = document.querySelector('.estimate-shipping .normal-estimate');
    const extendedDelivery = document.querySelector('.estimate-shipping .extended');
    
    if (!selectedOption) return;
    
    // Update visual state
    selectedOption.classList.add('none');
    selectedOption.classList.add('none-selected');
    selectedOption.setAttribute('data-handle', 'none');
    selectedOption.removeAttribute('data-title');
    selectedOption.removeAttribute('data-variant');
    
    // Hide custom button, show main ATC
    if (customBtn) customBtn.style.display = 'none';
    if (mainAtc) mainAtc.style.display = 'inline-flex';
    
    // Reset delivery display to normal
    if (estimateDelivery) estimateDelivery.style.display = 'block';
    if (extendedDelivery) extendedDelivery.style.display = 'none';
    
    // Update frame selection state
    window.frameSelection.clearFrame();
    
    // Update display
    selectedOption.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 60 60" fill="none">
        <rect width="60" height="60" fill="#F1F1F1"/>
        <path d="M59 1L1 59" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <div class="option--details">
        <span class="option-title">${t('product.frame_picker.no_frame', 'No Frame')}
        </span>
        <div class="option-frame-detail">
          <span class="glass-type">${t(
            'product.frame_picker.frame_own_print',
            'You will frame your own print'
          )}
          </span>
        </div>
      </div>`;
    
    log('Frame selection reset to No Frame');
  }

  /**
   * Updates all dropdown options with new variant prices and availability
   * @param {string} newVariantTitle - The new variant title (e.g., "100x140", "A4")
   */
  function updateAllDropdownOptions(newVariantTitle) {
    const dropdownOptions = document.querySelectorAll('.dropdown-option');
    const market = document.documentElement.getAttribute('data-market');
    
    dropdownOptions.forEach(option => {
      const optionHandle = option.getAttribute('data-handle');
      if (!optionHandle || optionHandle === 'none') return; // Skip "No Frame" option
      
      const optionVariant = option.querySelector('.selected-frame-variant');
      const optionSelectGlass = option.querySelector('.selected-glass');
      const optionPrice = option.querySelector('.frame-price');
      
      if (!optionVariant || !optionPrice) return;
      
      // Use frameData for frame product
      let frameProduct = frameData[optionHandle];
      if (frameProduct) {
        let matchedVariant = frameProduct.variants.find(
          variant => variant.title === newVariantTitle
        );
        
        // HIDE 100x140 IF NOT DK
        if (matchedVariant && matchedVariant.title === '100x140' && market !== 'dk') {
          option.style.display = 'none';
          return;
        }
        
        if (!matchedVariant) {
          option.style.display = 'none';
          return;
        }
        
        // Format the variant title
        let updatedMatchedVariantTitle;
        if (matchedVariant.title.includes('A')) {
          updatedMatchedVariantTitle = matchedVariant.title;
        } else {
          updatedMatchedVariantTitle = matchedVariant.title + ' cm';
        }
        
        // Get glass type
        let variantMetaValue;
        document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
          let variantMetaTitle = variantMeta.getAttribute('data-title');
          if (matchedVariant.title == variantMetaTitle) {
            variantMetaValue = variantMeta.getAttribute('data-glass');
          }
        });
        
        // Update option display
        option.style.display = 'flex';
        optionSelectGlass.textContent = variantMetaValue;
        optionVariant.textContent = updatedMatchedVariantTitle;
        option.setAttribute('value', matchedVariant.id);
        option.setAttribute('data-price', matchedVariant.price);
        option.setAttribute('data-compare', matchedVariant.compare_at_price);
        optionPrice.textContent = Woolman.Utils.formatMoney(
          matchedVariant.price,
          theme.settings.moneyFormat
        );
        
        log('Updated dropdown option for', optionHandle, 'with variant', newVariantTitle);
      }
    });
  }

  /**
   * Updates the frame dropdown display when the main variant size changes
   * @param {string} newVariantTitle - The new variant title (e.g., "100x140", "A4")
   */
  function updateFrameDropdownDisplay(newVariantTitle) {
    if (!window.frameSelection.isFrameSelected) return;
    
    const selectedOption = document.querySelector('.selected-option');
    if (!selectedOption || selectedOption.classList.contains('none')) return;
    
    const frameHandle = selectedOption.getAttribute('data-handle');
    const frameTitle = selectedOption.getAttribute('data-title');
    
    if (!frameHandle || !frameTitle) return;
    
    // Use frameData for frame product
    let frameProduct = frameData[frameHandle];
    if (frameProduct) {
      let matchedVariant = frameProduct.variants.find(
        variant => variant.title === newVariantTitle
      );
      
      if (matchedVariant) {
        // Update frame selection state
        window.frameSelection.framePrice = parseFloat(matchedVariant.price);
        window.frameSelection.frameComparePrice = parseFloat(matchedVariant.compare_at_price || 0);
        window.frameSelection.frameVariant = matchedVariant.title;
        window.frameSelection.frameId = matchedVariant.id;
        
        // Update price state
        window.priceState.framePrice = window.frameSelection.framePrice;
        window.priceState.frameComparePrice = window.frameSelection.frameComparePrice;
        
        // Trigger price update to sync UI
        window.priceState.updatePrices();
        
        // Format the variant title
        let updatedMatchedVariantTitle;
        if (matchedVariant.title.includes('A')) {
          updatedMatchedVariantTitle = matchedVariant.title;
        } else {
          updatedMatchedVariantTitle = matchedVariant.title + ' cm';
        }
        
        // Get glass type
        let variantMetaValue;
        document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
          let variantMetaTitle = variantMeta.getAttribute('data-title');
          if (matchedVariant.title == variantMetaTitle) {
            variantMetaValue = variantMeta.getAttribute('data-glass');
          }
        });
        
        // Update the selected option display
        const selectedFrameImage = selectedOption.querySelector('img').getAttribute('src');
        selectedOption.innerHTML = `<img src="${selectedFrameImage}">
          <div class="option--details">
            <span class="option-title">${frameTitle} - 
              <span class="selected-frame-variant">${updatedMatchedVariantTitle} </span>
            </span>
            <div class="option-frame-detail">
              <span class="glass-type">(${t(
                'product.glass_type',
                'Glass:'
              )} <span class="selected-glass">${variantMetaValue}</span>) + 
                <span class="frame-price">
                  ${Woolman.Utils.formatMoney(
                    matchedVariant.price,
                    theme.settings.moneyFormat
                  )}
                </span>
              </span>
            </div>
          </div>`;
        
        selectedOption.setAttribute('value', matchedVariant.id);
        
        // Update all dropdown options for the new variant
        updateAllDropdownOptions(newVariantTitle);
        
        log('Frame dropdown display updated for variant:', newVariantTitle, matchedVariant);
      } else {
        log('No matching frame variant found for:', newVariantTitle, 'Reverting to No Frame');
        // Revert to "No Frame" state when variant is not available for selected frame
        resetToNoFrame();
      }
    }
  }

  // ---------------
  // Dropdown Variant Price Update
  // ---------------
  // Price update for dropdown variants
  if (variantSelect) {
    /**
     * Handles variant selection changes from the dropdown.
     *
     * Fetches the product data for the selected variant, updates the price state,
     * and triggers a UI price update. Ensures only the latest request is processed
     * to avoid race conditions from rapid user input.
     *
     * Side Effects:
     * - Updates window.priceState with the selected variant's price and compare price.
     * - Triggers window.priceState.updatePrices() to update all price displays.
     * - Updates window.latestVariantId for tracking.
     */
    log('Attaching change event to variantSelect');
    variantSelect.addEventListener('change', function () {
      log('variantSelect changed', this.value);
      let selectedOption = this.value;
      let selectMainContainer = this.closest('variant-selects');
      let selectPrdHandle = selectMainContainer.getAttribute('data-handle');
      log('Fetching product for handle', selectPrdHandle);

      // Create a unique request identifier to prevent race conditions
      const requestId = Date.now();
      window.latestVariantSelectRequest = requestId;

      cachedFetch(`/products/${selectPrdHandle}.js`)
        .then(product => {
          // Only process the response if it matches the latest request
          if (window.latestVariantSelectRequest !== requestId) {
            log('Skipping outdated variant select response', requestId);
            return;
          }

          // Find the variant object matching the selected option
          let currentVariant = product.variants.find(variant => variant.title === selectedOption);
          log('Current variant found', currentVariant);
          if (!currentVariant) return;

          // Track the latest variant ID for other logic
          window.latestVariantId = currentVariant.id;

          // Update the global price state (ensures frame data is preserved)
          window.priceState.variantPrice = currentVariant.price;
          window.priceState.variantComparePrice = currentVariant.compare_at_price;

          // Update frame dropdown display if frame is selected (this may update frame prices)
          if (window.frameSelection.isFrameSelected) {
            const selectedOption = document.querySelector('.selected-option');
            if (selectedOption && !selectedOption.classList.contains('none')) {
              updateFrameDropdownDisplay(currentVariant.title);
            }
          } else {
            // If no frame selected, still update dropdown options for correct prices/availability
            updateAllDropdownOptions(currentVariant.title);
            // Then trigger price update
            window.priceState.updatePrices();
          }

          // Update available text count for dropdown options
          setTimeout(() => {
            const visibleCount = Array.from(document.querySelectorAll('.dropdown-option')).filter(
              opt => opt.style.display !== 'none'
            ).length;
            let actualCount = visibleCount - 1; // Subtract 1 for "No Frame" option
            log('Dropdown options updated via dropdown select, visible count', actualCount);
            if (availableText) {
              const optionText = actualCount == 1 ? 
                t('product.frame_picker.one_option_available', 'option available') :
                t('product.frame_picker.options_available', 'options available');
              
              log('Dropdown variant change - Translation result:', optionText);
              log('Dropdown variant change - Setting availableText to:', `${actualCount} ${optionText}`);
              
              availableText.textContent = `${actualCount} ${optionText}`;
            }
          }, 50); // Small delay to ensure dropdown options are updated first

          log('Prices updated via variantSelect', {
            variantId: currentVariant.id,
            variant: currentVariant.price,
            frame: window.frameSelection.isFrameSelected ? window.frameSelection.framePrice : 0,
            total: window.priceState.totalPrice,
          });
        });
    });
  }

  if (dropdownContainer) {
    log('Dropdown container found');
    if (customBtn) {
      priceContainer = customBtn.querySelector('.total-price');
      comparePriceContainer = customBtn.querySelector('.total-compare-price');
    }

    const sizePrice = document.querySelector('.size-total-price');

    let price = 0,
      comparePrice = 0,
      totalPrice = 0,
      totalComparePrice = 0,
      selectedFramePrice = 0;

    // Update available options count
    if (availableText && dropdownOptions.length > 0) {
      // Count visible options (excluding hidden 100x140 if not dk)
      const visibleOptions = Array.from(dropdownOptions).filter(opt => opt.style.display !== 'none').length;
      let actualCount = visibleOptions - 1; // Subtract 1 for "No Frame" option
      log('Setting available options text', {visibleOptions, dropdownOptionsLength: dropdownOptions.length, actualCount});
      log('Available text element', availableText);
      
      const optionText = actualCount == 1 ? 
        t('product.frame_picker.one_option_available', 'option available') :
        t('product.frame_picker.options_available', 'options available');
      
      log('Translation result:', optionText);
      log('Setting availableText to:', `${actualCount} ${optionText}`);
      
      availableText.textContent = `${actualCount} ${optionText}`;
    }

    // Toggle dropdown list visibility
    dropdownContainer.addEventListener('click', e => {
      log('Dropdown container clicked');
      dropdownList.classList.toggle('open');
      e.stopPropagation();
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', () => {
      log('Document clicked, closing dropdown');
      dropdownList.classList.remove('open');
    });

    /**
     * Handles changes to the product variant selection (radio inputs).
     *
     * This function is responsible for updating the UI and internal state when a user selects a different product variant (such as size or frame option).
     * It updates the price, frame selection, and availability display based on the selected variant.
     *
     * Main responsibilities:
     * - Fetches the relevant product and frame data for the selected variant.
     * - Updates global price state (`window.priceState`) and frame selection state (`window.frameSelection`).
     * - Updates the DOM to reflect the selected variant's price, frame, and glass type.
     * - Handles inventory and delivery estimate display for out-of-stock variants.
     * - Ensures dropdown options and their prices are updated for all frames.
     *
     * @param {HTMLInputElement} input - The radio input element that triggered the change event. Should represent a product variant.
     *
     * Side effects:
     * - Modifies DOM elements for price, frame, and delivery display.
     * - Updates global state objects: `window.priceState` and `window.frameSelection`.
     * - May trigger asynchronous fetches for product/frame data.
     *
     * Notes:
     * - Relies on global variables and helpers: `cachedFetch`, `frameData`, `dropdownOptions`, `updatePrices`, and translation helper `t()`.
     * - Handles both main product and frame product logic, including fallback fetches if frame data is not cached.
     * - Updates dropdown options to show only available variants.
     */
    function handleVariantChange(input) {
      log('handleVariantChange called', input.value);
      let selectedVariant = input.value;
      let variantContainer = input.closest('variant-radios');
      const optionVariant = document.querySelector('.selected-option');
      const selectedTitle = optionVariant.getAttribute('data-title');
      const selectedHandle = optionVariant.getAttribute('data-handle');
      const frameVariant = optionVariant.getAttribute('data-variant');
      let selectedFrameImage, selectedFrameTitle;

      if (!optionVariant.classList.contains('none')) {
        selectedFrameImage = optionVariant.querySelector('img').getAttribute('src');
        selectedFrameTitle = optionVariant.querySelector('.option-title').textContent;
      }

      if (!variantContainer) {
        log('No variantContainer found');
        return;
      }
      let productHandle = variantContainer.getAttribute('data-handle');
      if (!productHandle) {
        log('No productHandle found');
        return;
      }

      // Use cachedFetch for main product
      cachedFetch(`/products/${productHandle}.js`)
        .then(product => {
          let currentVariant = product.variants.find(variant => variant.title === selectedVariant);
          log('Current variant for radios', currentVariant);
          if (!currentVariant) return;

          // Update the price state instead of local variables
          window.priceState.variantPrice = currentVariant.price;
          window.priceState.variantComparePrice = currentVariant.compare_at_price;

          // If we have a frame selected, the framePrice properties will already be set
          // If not, they'll be 0 because of the initial state
          log('Price state before update', {
            variant: window.priceState.variantPrice,
            frame: window.priceState.framePrice,
            isFrameSelected: window.frameSelection.isFrameSelected,
          });

          // This call respects any existing frame selection
          window.priceState.updatePrices();

          if (optionVariant.classList.contains('none')) {
            log('Updated prices for none frame');
          }

          if (!optionVariant.classList.contains('none')) {
            log('Fetching selected frame handle', selectedHandle);
            // Use frameData for frame product
            let frameProduct = frameData[selectedHandle];
            if (frameProduct) {
              let matchedVariant = frameProduct.variants.find(
                variant => variant.title === selectedVariant
              );
              log('Matched variant for frame', matchedVariant);

              // We have a frame selected and variant changed - update frame prices
              if (matchedVariant) {
                // Update frame price in frameSelection
                window.frameSelection.framePrice = parseFloat(matchedVariant.price);
                window.frameSelection.frameComparePrice = parseFloat(
                  matchedVariant.compare_at_price || 0
                );
                // Update priceState with frame data
                window.priceState.framePrice = window.frameSelection.framePrice;
                window.priceState.frameComparePrice = window.frameSelection.frameComparePrice;
                // Update prices
                window.priceState.updatePrices();

                // Check inventory directly from the variant data
                log(
                  'Checking inventory from API response:',
                  matchedVariant.inventory_policy,
                  matchedVariant.inventory_quantity
                );

                if (
                  matchedVariant.inventory_policy == 'continue' &&
                  matchedVariant.inventory_quantity <= 0
                ) {
                  estimateDelivery.style.display = 'none';
                  extendedDelivery.style.display = 'block';
                } else {
                  estimateDelivery.style.display = 'block';
                  extendedDelivery.style.display = 'none';
                }
              }

              if (!matchedVariant) {
                log('No matched variant, resetting to No Frame');
                // Reset to "No Frame" state properly
                optionVariant.classList.add('none');
                optionVariant.setAttribute('data-handle', 'none');
                optionVariant.removeAttribute('data-title');
                optionVariant.classList.add('none-selected');
                optionVariant.removeAttribute('data-variant');
                customBtn.style.display = 'none';
                mainAtc.style.display = 'inline-flex';

                optionVariant.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 60 60" fill="none">
                    <rect width="60" height="60" fill="#F1F1F1"/>
                    <path d="M59 1L1 59" stroke="white" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                <div class="option--details">
                  <span class="option-title">${t('product.frame_picker.no_frame', 'No Frame')}
                  </span>
                  <div class="option-frame-detail">
                    <span class="glass-type">${t(
                      'product.frame_picker.frame_own_print',
                      'You will frame your own print'
                    )}
                    </span>
                  </div>
                </div>`;

                // Reset delivery display to normal when frame is cleared
                estimateDelivery.style.display = 'block';
                extendedDelivery.style.display = 'none';

                // Use unified price update function with compare price
                updatePrices(price, 0, comparePrice);

                // Update global frameSelection object
                window.frameSelection.isFrameSelected = false;
                window.frameSelection.framePrice = 0;
                window.frameSelection.frameHandle = null;
                window.frameSelection.frameTitle = null;
                window.frameSelection.frameVariant = null;
                window.frameSelection.frameId = null;
                window.frameSelection.frameComparePrice = 0;
              } else {
                selectedFramePrice = matchedVariant.price;
                priceContainer.setAttribute('data-price', price);
                totalPrice = parseFloat(price) + parseFloat(selectedFramePrice);

                // Sync frame selection state with chosen variant
                window.frameSelection.isFrameSelected = true;
                window.frameSelection.framePrice = parseFloat(selectedFramePrice);
                window.frameSelection.frameComparePrice = parseFloat(
                  matchedVariant.compare_at_price || 0
                );
                window.frameSelection.frameHandle = selectedHandle;
                window.frameSelection.frameTitle = selectedTitle;
                window.frameSelection.frameVariant = matchedVariant.title;
                window.frameSelection.frameId = matchedVariant.id;

                // Always use updatePrices to ensure UI reflects current selection
                const matchedFrameComparePrice = matchedVariant.compare_at_price || 0;
                updatePrices(price, selectedFramePrice, comparePrice, matchedFrameComparePrice);

                optionVariant.setAttribute('value', matchedVariant.id);
                let variantMetaValue;
                document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
                  let variantMetaTitle = variantMeta.getAttribute('data-title');
                  if (matchedVariant.title == variantMetaTitle) {
                    variantMetaValue = variantMeta.getAttribute('data-glass');
                  }
                });
                let updatedMatchedVariantTitle;
                log('v', matchedVariant.title);
                if (matchedVariant.title.includes('A')) {
                  updatedMatchedVariantTitle = matchedVariant.title;
                } else {
                  updatedMatchedVariantTitle = matchedVariant.title + ' cm';
                }
                optionVariant.innerHTML = `<img src="${selectedFrameImage}">
                  <div class="option--details">
                    <span class="option-title">${selectedTitle} - 
                      <span class="selected-frame-variant">${updatedMatchedVariantTitle} </span>
                    </span>
                    <div class="option-frame-detail">
                      <span class="glass-type">(${t(
                        'product.glass_type',
                        'Glass:'
                      )} <span class="selected-glass">${variantMetaValue}</span>) + 
                        <span class="frame-price">
                          ${Woolman.Utils.formatMoney(
                            matchedVariant.price,
                            theme.settings.moneyFormat
                          )}
                        </span>
                      </span>
                    </div>
                  </div>`;
              }
            } else {
              // fallback to cachedFetch if not found in frameData
              cachedFetch(`/products/${selectedHandle}.js`).then(frameProduct => {
                let matchedVariant = frameProduct.variants.find(
                  variant => variant.title === selectedVariant
                );
                log('Matched variant for frame', matchedVariant);

                // Similar to above, update the frame price if frame is found in fetch
                if (matchedVariant) {
                  // Update frame price in frameSelection
                  window.frameSelection.framePrice = parseFloat(matchedVariant.price);
                  window.frameSelection.frameComparePrice = parseFloat(
                    matchedVariant.compare_at_price || 0
                  );
                  // Update priceState with frame data
                  window.priceState.framePrice = window.frameSelection.framePrice;
                  window.priceState.frameComparePrice = window.frameSelection.frameComparePrice;
                  // Update prices
                  window.priceState.updatePrices();

                  // Check inventory directly from the variant data
                  log(
                    'Checking inventory from API response:',
                    matchedVariant.inventory_policy,
                    matchedVariant.inventory_quantity
                  );

                  if (
                    matchedVariant.inventory_policy == 'continue' &&
                    matchedVariant.inventory_quantity <= 0
                  ) {
                    estimateDelivery.style.display = 'none';
                    extendedDelivery.style.display = 'block';
                  } else {
                    estimateDelivery.style.display = 'block';
                    extendedDelivery.style.display = 'none';
                  }
                }

                if (!matchedVariant) {
                  log('No matched variant, resetting to No Frame');
                  // Reset to "No Frame" state properly
                  optionVariant.classList.add('none');
                  optionVariant.setAttribute('data-handle', 'none');
                  optionVariant.removeAttribute('data-title');
                  optionVariant.classList.add('none-selected');
                  optionVariant.removeAttribute('data-variant');
                  customBtn.style.display = 'none';
                  mainAtc.style.display = 'inline-flex';

                  optionVariant.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 60 60" fill="none">
                      <rect width="60" height="60" fill="#F1F1F1"/>
                      <path d="M59 1L1 59" stroke="white" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  <div class="option--details">
                    <span class="option-title">${t('product.frame_picker.no_frame', 'No Frame')}
                    </span>
                    <div class="option-frame-detail">
                      <span class="glass-type">${t(
                        'product.frame_picker.frame_own_print',
                        'You will frame your own print'
                      )}
                      </span>
                    </div>
                  </div>`;

                  // Reset delivery display to normal when frame is cleared
                  estimateDelivery.style.display = 'block';
                  extendedDelivery.style.display = 'none';

                  // Use unified price update function with compare price
                  updatePrices(price, 0, comparePrice);

                  // Update global frameSelection object
                  window.frameSelection.isFrameSelected = false;
                  window.frameSelection.framePrice = 0;
                  window.frameSelection.frameHandle = null;
                  window.frameSelection.frameTitle = null;
                  window.frameSelection.frameVariant = null;
                  window.frameSelection.frameId = null;
                  window.frameSelection.frameComparePrice = 0;
                } else {
                  selectedFramePrice = matchedVariant.price;
                  priceContainer.setAttribute('data-price', price);
                  totalPrice = parseFloat(price) + parseFloat(selectedFramePrice);

                  // Update global frameSelection object
                  window.frameSelection.isFrameSelected = true;
                  window.frameSelection.framePrice = parseFloat(selectedFramePrice);
                  window.frameSelection.frameComparePrice = parseFloat(
                    matchedVariant.compare_at_price || 0
                  );
                  window.frameSelection.frameHandle = selectedHandle;
                  window.frameSelection.frameTitle = selectedTitle;
                  window.frameSelection.frameVariant = matchedVariant.title;
                  window.frameSelection.frameId = matchedVariant.id;

                  // Use the unified update function for combined prices with compare price
                  const matchedFrameComparePrice = matchedVariant.compare_at_price || 0;
                  updatePrices(price, selectedFramePrice, comparePrice, matchedFrameComparePrice);

                  optionVariant.setAttribute('value', matchedVariant.id);
                  let variantMetaValue;
                  document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
                    let variantMetaTitle = variantMeta.getAttribute('data-title');
                    if (matchedVariant.title == variantMetaTitle) {
                      variantMetaValue = variantMeta.getAttribute('data-glass');
                    }
                  });
                  let updatedMatchedVariantTitle;
                  log('v', matchedVariant.title);
                  if (
                    matchedVariant.title == 'A1' ||
                    matchedVariant.title == 'A2' ||
                    matchedVariant.title == 'A3' ||
                    matchedVariant.title == 'A4'
                  ) {
                    updatedMatchedVariantTitle = matchedVariant.title;
                  } else {
                    updatedMatchedVariantTitle = matchedVariant.title + ' cm';
                  }
                  optionVariant.innerHTML = `<img src="${selectedFrameImage}">
                    <div class="option--details">
                      <span class="option-title">${selectedTitle} - 
                        <span class="selected-frame-variant">${updatedMatchedVariantTitle} </span>
                      </span>
                      <div class="option-frame-detail">
                        <span class="glass-type">(${t(
                          'product.glass_type',
                          'Glass:'
                        )} <span class="selected-glass">${variantMetaValue}</span>) + 
                          <span class="frame-price">
                            ${Woolman.Utils.formatMoney(
                              matchedVariant.price,
                              theme.settings.moneyFormat
                            )}
                          </span>
                        </span>
                      </div>
                    </div>`;
                }
              });
            }
          }
        })
        .catch(error => {
          log('Error in radios fetch', error);
        });

      const fetchPromises = [];

      dropdownOptions.forEach(option => {
        const optionHandle = option.getAttribute('data-handle');
        if (!optionHandle) return;

        const optionVariant = option.querySelector('.selected-frame-variant');
        const optionSelectGlass = option.querySelector('.selected-glass');
        const optionPrice = option.querySelector('.frame-price');

        // Use frameData for frame product
        let frameProduct = frameData[optionHandle];
        if (frameProduct) {
          let matchedVariant = frameProduct.variants.find(
            variant => variant.title === selectedVariant
          );
          
          // HIDE 100x140 IF NOT DK
          if (matchedVariant && matchedVariant.title === '100x140' && market !== 'dk') {
            option.style.display = 'none';
            return;
          }
          
          log('Dropdown option fetch', optionHandle, matchedVariant);
          if (!matchedVariant) {
            option.style.display = 'none';
            return;
          }
          let variantMetaValue;
          document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
            let variantMetaTitle = variantMeta.getAttribute('data-title');
            if (matchedVariant.title == variantMetaTitle) {
              variantMetaValue = variantMeta.getAttribute('data-glass');
            }
          });
          let updatedMatchedVariantTitle;
          if (matchedVariant.title.includes('A')) {
            updatedMatchedVariantTitle = matchedVariant.title;
          } else {
            updatedMatchedVariantTitle = matchedVariant.title + ' cm';
          }
          option.style.display = 'flex';
          optionSelectGlass.textContent = variantMetaValue;
          optionVariant.textContent = updatedMatchedVariantTitle;
          option.setAttribute('value', matchedVariant.id);
          option.setAttribute('data-price', matchedVariant.price);
          option.setAttribute('data-compare', matchedVariant.compare_at_price);
          optionPrice.textContent = Woolman.Utils.formatMoney(
            matchedVariant.price,
            theme.settings.moneyFormat
          );
        } else {
          // fallback to cachedFetch if not found in frameData
          const fetchPromise = cachedFetch(`/products/${optionHandle}.js`).then(product => {
            let matchedVariant = product.variants.find(
              variant => variant.title === selectedVariant
            );
            
            // HIDE 100x140 IF NOT DK
            if (matchedVariant && matchedVariant.title === '100x140' && market !== 'dk') {
              option.style.display = 'none';
              return;
            }
            
            log('Dropdown option fetch', optionHandle, matchedVariant);
            if (!matchedVariant) {
              option.style.display = 'none';
              return;
            }
            let variantMetaValue;
            document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
              let variantMetaTitle = variantMeta.getAttribute('data-title');
              if (matchedVariant.title == variantMetaTitle) {
                variantMetaValue = variantMeta.getAttribute('data-glass');
              }
            });
            let updatedMatchedVariantTitle;
            if (matchedVariant.title.includes('A')) {
              updatedMatchedVariantTitle = matchedVariant.title;
            } else {
              updatedMatchedVariantTitle = matchedVariant.title + ' cm';
            }
            option.style.display = 'flex';
            optionSelectGlass.textContent = variantMetaValue;
            optionVariant.textContent = updatedMatchedVariantTitle;
            option.setAttribute('value', matchedVariant.id);
            option.setAttribute('data-price', matchedVariant.price);
            option.setAttribute('data-compare', matchedVariant.compare_at_price);
            optionPrice.textContent = Woolman.Utils.formatMoney(
              matchedVariant.price,
              theme.settings.moneyFormat
            );
          });
          fetchPromises.push(fetchPromise);
        }
      });

      Promise.all(fetchPromises).then(() => {
        const visibleCount = Array.from(document.querySelectorAll('.dropdown-option')).filter(
          opt => opt.style.display !== 'none'
        ).length;
        let actualCount = visibleCount - 1;
        log('Dropdown options updated, visible count', actualCount);
        log('Available text element', availableText);
        if (actualCount == 1) {
          availableText.textContent = `${actualCount} ${t(
            'product.frame_picker.one_option_available',
            'option available'
          )}`;
        } else {
          availableText.textContent = `${actualCount} ${t(
            'product.frame_picker.options_available',
            'options available'
          )}`;
        }
      });
    }

    // Attach event listener and also trigger on load
    document.querySelectorAll(".product-variant-options input[type='radio']").forEach(input => {
      input.addEventListener('change', function () {
        log('Radio input changed', input.value);
        handleVariantChange(input);
      });

      // Trigger on load if checked
      if (input.checked) {
        log('Radio input checked on load', input.value);
        handleVariantChange(input);
      }
    });

    // Handle option selection
    dropdownOptions.forEach(option => {
      // Hide 100x140 options on initial load if not dk market
      const optionVariantText = option.querySelector('.selected-frame-variant')?.textContent;
      if (optionVariantText && optionVariantText.trim() === '100x140 cm' && market !== 'dk') {
        option.style.display = 'none';
      }
      
      option.addEventListener('click', e => {
        log('Dropdown option clicked', option.getAttribute('data-title'));
        const optionTitle = option.getAttribute('data-title');
        const mediaSlider = document.querySelector('.product-media-slider');
        e.stopPropagation();

        const isNoFrame = option.getAttribute('data-value') === 'none';
        selectedOption.classList.toggle('none', isNoFrame);

        if (isNoFrame) {
          log('No frame selected');
          selectedOption.classList.add('none-selected');
          selectedOption.setAttribute('data-handle', 'none');
          selectedOption.removeAttribute('data-title');
          selectedOption.removeAttribute('data-variant');
          customBtn.style.display = 'none';
          mainAtc.style.display = 'inline-flex';
          selectedOption.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 60 60" fill="none">
                <rect width="60" height="60" fill="#F1F1F1"/>
                <path d="M59 1L1 59" stroke="white" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <div class="option--details">
                <span class="option-title">${t('product.frame_picker.no_frame', 'No Frame')}
                </span>
                <div class="option-frame-detail">
                  <span class="glass-type">${t(
                    'product.frame_picker.frame_own_print',
                    'You will frame your own print'
                  )}
                  </span>
                </div>
              </div>`;

          // Update global frameSelection object
          window.frameSelection.isFrameSelected = false;
          window.frameSelection.framePrice = 0;
          window.frameSelection.frameHandle = 'none';
          window.frameSelection.frameTitle = null;
          window.frameSelection.frameVariant = null;
          window.frameSelection.frameId = null;
          window.frameSelection.frameComparePrice = 0;

          // Reset delivery display to normal when no frame is selected
          estimateDelivery.style.display = 'block';
          extendedDelivery.style.display = 'none';

          mediaSlider.scrollToSlide(0);

          const selectedMainVariantHandle = getCurrentProductHandle();
          const selectedMainVariant = getCurrentVariantValue();
          
          if (selectedMainVariantHandle && selectedMainVariant) {
            log('Fetching for no frame, main variant', selectedMainVariant);
            fetch(`/products/${selectedMainVariantHandle}.js`)
              .then(response => response.json())
              .then(product => {
                let matchedVariant = product.variants.find(
                  variant => variant.title === selectedMainVariant
                );
                if (!matchedVariant) return;

                let totalAddedPrice = matchedVariant.price;
                // Use unified price update function with possible compare price
                updatePrices(totalAddedPrice, 0, matchedVariant.compare_at_price);
              })
              .catch(error => log('Error in no frame fetch', error));
          }
        } else {
          log('Frame selected', optionTitle);
          selectedOption.classList.remove('none-selected');
          customBtn.style.display = 'inline-flex';
          mainAtc.style.display = 'none';

          const optionHandle = option.getAttribute('data-handle');
          const optionVariant = option.querySelector('.selected-frame-variant').textContent;
          const framePrice = option.getAttribute('data-price');
          const frameComparePrice = option.getAttribute('data-compare');
          const optionImage = option.querySelector('img').getAttribute('src');
          const frameId = option.getAttribute('value');
          selectedOption.setAttribute('data-handle', optionHandle);
          selectedOption.setAttribute('data-title', optionTitle);
          selectedOption.setAttribute('data-variant', optionVariant);

          // Update global frameSelection object
          window.frameSelection.isFrameSelected = true;
          window.frameSelection.framePrice = parseFloat(framePrice);
          window.frameSelection.frameComparePrice = parseFloat(frameComparePrice || 0);
          window.frameSelection.frameHandle = optionHandle;
          window.frameSelection.frameTitle = optionTitle;
          window.frameSelection.frameVariant = optionVariant;
          window.frameSelection.frameId = frameId;

          // Check inventory using frameData
          const frameDataObj = window.frameData[optionHandle];
          if (frameDataObj && frameDataObj.variants) {
            // Find the matching variant by ID
            const frameVariant = frameDataObj.variants.find(v => v.id == frameId);
            if (frameVariant) {
              log(
                'Checking inventory for selected frame with frameData structure',
                frameVariant.inventory_policy,
                frameVariant.inventory_quantity
              );

              if (
                frameVariant.inventory_policy == 'continue' &&
                frameVariant.inventory_quantity <= 0
              ) {
                estimateDelivery.style.display = 'none';
                extendedDelivery.style.display = 'block';
              } else {
                estimateDelivery.style.display = 'block';
                extendedDelivery.style.display = 'none';
              }
            }
          } else {
            // Fallback to variant JSON if frameData isn't working
            const variantJsonData = document.querySelector('.variant-json').textContent;
            let parsedData = [];
            try {
              parsedData = JSON.parse(variantJsonData);
            } catch (e) {
              log('[PEL] Failed to parse variant JSON data', e);
            }

            parsedData.forEach(variant => {
              if (variant.variant_id == frameId) {
                log(
                  'Fallback: checking inventory from variantJSON',
                  variant.inventory_policy,
                  variant.inventory_quantity
                );
                if (variant.inventory_policy == 'continue' && variant.inventory_quantity <= 0) {
                  estimateDelivery.style.display = 'none';
                  extendedDelivery.style.display = 'block';
                } else {
                  estimateDelivery.style.display = 'block';
                  extendedDelivery.style.display = 'none';
                }
              }
            });
          }

          if (optionHandle == 'lys-egetraes-ramme') {
            mediaSlider.scrollToSlide(0);
          } else if (optionHandle == 'mork-egetrae-ramme') {
            mediaSlider.scrollToSlide(5);
          } else if (optionHandle == 'sort-ramme') {
            mediaSlider.scrollToSlide(4);
          }
          let variantMetaSelect;
          document.querySelectorAll('.variant-meta-content').forEach(variantMeta => {
            let variantMetaTitle = variantMeta.getAttribute('data-title');
            let strippedVariant = optionVariant.replace(' cm', '').trim();
            if (strippedVariant == variantMetaTitle) {
              variantMetaSelect = variantMeta.getAttribute('data-glass');
            }
          });

          selectedOption.innerHTML = `<img src="${optionImage}">
              <div class="option--details">
                <span class="option-title">${optionTitle} - <span class="selected-frame-variant">${optionVariant}</span></span>
                <div class="option-frame-detail">
                  <span class="glass-type">(${t(
                    'product.glass_type',
                    'Glass:'
                  )} <span class="selected-glass">${variantMetaSelect}</span>) + <span class="frame-price">${Woolman.Utils.formatMoney(
            framePrice,
            theme.settings.moneyFormat
          )}</span></span>
                </div>
              </div>`;

          const selectedMainVariantHandle = getCurrentProductHandle();
          const selectedMainVariant = getCurrentVariantValue();
          
          if (selectedMainVariantHandle && selectedMainVariant) {
            log('Fetching for frame, main variant', selectedMainVariant);
            fetch(`/products/${selectedMainVariantHandle}.js`)
              .then(response => response.json())
              .then(product => {
                let matchedVariant = product.variants.find(
                  variant => variant.title === selectedMainVariant
                );
                if (!matchedVariant) return;

                // Use unified price update function with frame price and compare prices
                const frameComparePrice = option.getAttribute('data-compare') || 0;
                updatePrices(
                  matchedVariant.price,
                  framePrice,
                  matchedVariant.compare_at_price,
                  frameComparePrice
                );
              })
              .catch(error => log('Error in frame fetch', error));
          }

          // Compare price is now handled in updatePrices
          selectedOption.setAttribute('value', frameId);

          // Prevent duplicate listeners by removing any previous handler first
          const customBtnEl = document.querySelector('.custom-btn');
          if (customBtnEl) {
            if (customBtnEl._customAddToCartHandler) {
              customBtnEl.removeEventListener('click', customBtnEl._customAddToCartHandler);
            }
            customBtnEl._customAddToCartHandler = function () {
              log('Custom add to cart clicked', selectedOption.getAttribute('value'));
              addToCart(selectedOption.getAttribute('value'));
            };
            customBtnEl.addEventListener('click', customBtnEl._customAddToCartHandler);
          }
        }

        dropdownList.classList.remove('open');
      });
    });

    async function addToCart(variantId, quantity = 1) {
      log('addToCart called', variantId, quantity);
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: variantId, quantity: quantity }),
        });
        mainAtc.click();
        log('Add to cart response', response);
        return await response.json();
      } catch (error) {
        log('Add to Cart Error:', error);
        // Show user feedback on error
        const btn = document.querySelector('.custom-btn');
        if (btn) {
          const originalText = btn.textContent;
          btn.textContent = typeof t === 'function' ? t('cart.error', 'Error adding to cart') : 'Error adding to cart';
          btn.classList.add('error');
          setTimeout(() => {
            btn.textContent = typeof t === 'function' ? t('cart.add_to_cart', 'Add to cart') : 'Add to cart';
            btn.classList.remove('error');
          }, 2000);
        }
      }
    }
  }

  // Product Media JS
  const mediaContainer = document.querySelector('.product-media-slider .gridy-track');
  const prevButton = document.querySelector('.slider-prev-arrow');
  const nextButton = document.querySelector('.slider-next-arrow');
  const activeSlideCountEl = document.querySelector('.active-slide-count');
  const totalSlideCountEl = document.querySelector('.total-slide-count');
  const ogPrevBtn = mediaContainer?.parentElement.querySelector('button[data-direction="prev"]');
  const ogNextBtn = mediaContainer?.parentElement.querySelector('button[data-direction="next"]');

  if (
    mediaContainer &&
    prevButton &&
    nextButton &&
    activeSlideCountEl &&
    totalSlideCountEl &&
    ogPrevBtn &&
    ogNextBtn
  ) {
    // Get all media items
    const mediaItems = mediaContainer.querySelectorAll('.product-media');
    if (mediaItems.length > 0) {
      const updatedMediaLength = mediaItems.length;

      // Set total slide count with translated "of"
      const ofText = document.querySelector('.slider-of');
      if (ofText) {
        ofText.textContent = t('general.slider.of', 'of');
      }
      totalSlideCountEl.textContent = updatedMediaLength;

      // Initialize active slide index
      let currentSlideIndex = 0;

      function updateArrowVisibility() {
        // Hide previous arrow when on first slide
        prevButton.style.visibility = currentSlideIndex === 0 ? 'hidden' : 'visible';
        // Hide next arrow when on last slide
        nextButton.style.visibility =
          currentSlideIndex === updatedMediaLength - 1 ? 'hidden' : 'visible';
      }

      updateArrowVisibility();

      // Update active slide count
      function updateActiveSlideCount() {
        activeSlideCountEl.textContent = currentSlideIndex + 1;
      }

      // Navigate to next slide
      function nextSlide() {
        currentSlideIndex = (currentSlideIndex + 1) % updatedMediaLength;
        scrollToSlide();
        updateArrowVisibility();
      }

      // Navigate to previous slide
      function prevSlide() {
        currentSlideIndex = (currentSlideIndex - 1 + updatedMediaLength) % updatedMediaLength; // Fixed calculation
        scrollToSlide();
        updateArrowVisibility();
      }

      // Scroll to the current slide
      function scrollToSlide() {
        if (mediaItems[currentSlideIndex]) {
          mediaItems[currentSlideIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
          });
          updateActiveSlideCount();
        }
      }

      // Add function to media slider to scroll to specific index
      mediaContainer.scrollToSlide = function (index) {
        if (index >= 0 && index < mediaItems.length) {
          currentSlideIndex = index;
          scrollToSlide();
          updateArrowVisibility();
        }
      };

      // Add event listeners to navigation buttons
      nextButton.addEventListener('click', nextSlide);
      prevButton.addEventListener('click', prevSlide);
      ogPrevBtn.addEventListener('click', function () {
        currentSlideIndex = (currentSlideIndex - 1 + updatedMediaLength) % updatedMediaLength;
        updateActiveSlideCount();
      });
      ogNextBtn.addEventListener('click', function () {
        currentSlideIndex = (currentSlideIndex + 1) % updatedMediaLength;
        updateActiveSlideCount();
      });

      // Initial active slide count
      updateActiveSlideCount();

      mediaContainer.addEventListener('scroll', () => {
        let closestIndex = 0;
        let closestDistance = Infinity;

        mediaItems.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          const distance = Math.abs(rect.left - mediaContainer.getBoundingClientRect().left);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        currentSlideIndex = closestIndex;
        updateActiveSlideCount();
        updateArrowVisibility();
      });
    }
  }

  // Size Drawer
  if (window.sizeDrawers && Array.isArray(window.sizeDrawers)) {
    window.sizeDrawers.forEach(drawerConfig => {
      const drawerName = drawerConfig.drawer_name;

      const btn = document.querySelector(`.${drawerName}`);
      const container = document.querySelector(`.size-drawer--main-container.${drawerName}`);

      if (!btn || !container) return;

      const content = container.querySelector('.size-drawer-panel');
      const header = document.querySelector('.site-header');
      const body = document.body;
      const closeBtn = container.querySelector('.drawer-close--btn');

      btn.addEventListener('click', () => {
        container.classList.add('size-drawer--open');
        content.style.transform = 'translate(0)';
        header.classList.add('header-back');
        body.classList.add('overflow-hidden');
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          closeDrawer(container, content, header, body);
        });
      }

      container.addEventListener('click', event => {
        if (!content.contains(event.target)) {
          closeDrawer(container, content, header, body);
        }
      });
    });
  }

  function closeDrawer(container, content, header, body) {
    setTimeout(() => {
      content.style.transform = 'translate(100%)';
      header.classList.remove('header-back');
      body.classList.remove('overflow-hidden');
      container.classList.remove('size-drawer--open');
    }, 300);
  }

  // Size variant selector
  const sizeVar = document.querySelector('.product-form-label--Size .product-variant-options');
  if (sizeVar) {
    const allSizeBtn = sizeVar.querySelector('.size-drawer');
    const sizeVariantContainer = sizeVar.querySelector('.prd-variant-Size');

    if (allSizeBtn && sizeVariantContainer) {
      allSizeBtn.addEventListener('click', function () {
        sizeVariantContainer.style.flex = '0 0 100%';
        document.querySelectorAll('.prd-variant-Size label').forEach(variantLabel => {
          variantLabel.style.display = ' block';
        });
        allSizeBtn.style.display = 'none';
      });
    }
  }

  // Gridy Slider Button Disable Functionality
  const gridyPrevButtons = document.querySelectorAll('.gridy-arrows button[data-direction="prev"]');
  if (gridyPrevButtons.length > 0) {
    gridyPrevButtons.forEach(prevBtn => {
      prevBtn.style.visibility = 'hidden';
      prevBtn.disabled = true; // Disable on first load
      const prcPrev = prevBtn.closest('.prc-prev'); // Find closest .prc-prev
      if (prcPrev) prcPrev.classList.add('disabled'); // Disable .prc-prev initially

      const gridyBtnContainer = prevBtn.parentElement;
      if (!gridyBtnContainer) return;

      const gridySlash = gridyBtnContainer.querySelector('.slash');
      if (gridySlash) gridySlash.style.display = 'none';

      const nextBtn = gridyBtnContainer.querySelector('button[data-direction="next"]');
      const slash = gridyBtnContainer.querySelector('.slash');
      if (!nextBtn) return;

      const updatePrevButtonState = () => {
        const gridyContainer = nextBtn.parentElement?.parentElement;
        if (!gridyContainer) return;

        const gridyTrack = gridyContainer.querySelector('.gridy-track');
        if (!gridyTrack || !gridyTrack.children.length) return;

        const firstChild = gridyTrack.children[0];
        const lastChild = gridyTrack.lastElementChild;
        const hasActiveChild = Array.from(gridyTrack.children).some(child =>
          child.classList.contains('active')
        );

        if (firstChild?.classList.contains('active')) {
          prevBtn.disabled = true;
          prevBtn.style.visibility = 'hidden';
          if (prcPrev) prcPrev.classList.add('disabled');
          if (gridySlash) gridySlash.style.display = 'none';
        } else if (hasActiveChild) {
          prevBtn.disabled = false;
          prevBtn.style.visibility = 'visible';
          if (prcPrev) prcPrev.classList.remove('disabled');
          if (gridySlash) gridySlash.style.display = 'block';
        } else {
          prevBtn.style.visibility = 'hidden';
          prevBtn.disabled = true;
          if (prcPrev) prcPrev.classList.add('disabled');
          if (gridySlash) gridySlash.style.display = 'none';
        }
        if (lastChild?.classList.contains('active')) {
          nextBtn.style.visibility = 'hidden';
          nextBtn.disabled = true;
        } else {
          nextBtn.style.visibility = 'visible';
          nextBtn.disabled = false;
        }
      };

      nextBtn.addEventListener('click', updatePrevButtonState);
      prevBtn.addEventListener('click', updatePrevButtonState);

      nextBtn.addEventListener('click', function () {
        const gridyContainer = nextBtn.parentElement?.parentElement;
        if (!gridyContainer) return;

        const gridyTrack = gridyContainer.querySelector('.gridy-track');

        if (gridyTrack) {
          // Prevent MutationObserver leak by disconnecting previous observer
          if (gridyTrack._prevObserver) {
            gridyTrack._prevObserver.disconnect();
          }
          const observer = new MutationObserver(updatePrevButtonState);
          observer.observe(gridyTrack, {
            childList: true,
            attributes: true,
            subtree: true,
          });
          gridyTrack._prevObserver = observer;
        }
        updatePrevButtonState();
      });
    });
  }

  // PRC button container
  const prcBtnContainer = document.querySelector('.prc-main-product');

  if (prcBtnContainer) {
    const prcMainContainer = prcBtnContainer.parentElement;
    const mainRecContainer = prcBtnContainer.parentElement;
    if (!mainRecContainer) return;

    const prcPrev = prcBtnContainer.querySelector('.prc-prev');
    const prcNext = prcBtnContainer.querySelector('.prc-next');
    prcPrev.disabled = true;

    if (prcPrev) {
      prcPrev.addEventListener('click', function () {
        const prevBtn = mainRecContainer.querySelector('button[data-direction="prev"]');
        if (prevBtn) prevBtn.click();
        updatePrcBtnState();
      });
    }

    if (prcNext) {
      prcNext.addEventListener('click', function () {
        const nextBtn = mainRecContainer.querySelector('button[data-direction="next"]');
        if (nextBtn) nextBtn.click();
        updatePrcBtnState();
      });
    }
    function updatePrcBtnState() {
      const prdGridyTrack = prcMainContainer.querySelector('.gridy-track');
      if (!prdGridyTrack || !prdGridyTrack.children.length) return;
      const prcFirstChild = prdGridyTrack.children[0];
      const prcLastChild = prdGridyTrack.lastElementChild;
      const prcActiveChild = Array.from(prdGridyTrack.children).some(child =>
        child.classList.contains('active')
      );
      if (prcFirstChild?.classList.contains('active')) {
        prcPrev.disabled = true;
      } else if (prcActiveChild) {
        prcPrev.disabled = false;
      } else {
        prcPrev.disabled = true;
      }
      if (prcLastChild?.classList.contains('active')) {
        prcNext.disabled = true;
      } else {
        prcNext.disabled = false;
      }
    }
  }

  // Rich Text Truncate Words Functionality
  document.querySelectorAll('.richtext--text-wrapper').forEach(function (wrapper) {
    if (!wrapper) return;

    const btn = wrapper.querySelector('.read-more-btn');
    const truncatedText = wrapper.querySelector('.truncated-text .metafield-rich_text_field');
    const fullText = wrapper.querySelector('.full-text');

    if (btn && truncatedText && fullText) {
      btn.addEventListener('click', function () {
        truncatedText.classList.toggle('hidden');
        fullText.classList.toggle('hidden');
        // Check if theme has button_label variable, otherwise use hardcoded text
        const readMoreLabel = t('buttons.read_more', 'Read More');
        btn.textContent = truncatedText.classList.contains('hidden')
          ? t('buttons.read_less', 'Read Less')
          : readMoreLabel;
      });
    }
  });
});

// Community Slider Section
let marqContent = document.querySelector('.marq-content');
if (marqContent) {
  let comSecHeading = document.querySelector('.slider-heading');

  if (marqContent.childElementCount > 1) {
    comSecHeading.style.display = 'block';
  } else {
    comSecHeading.style.display = 'none';
  }
}

// Header JS
document.querySelectorAll('.menu-item.has-megamenu').forEach(item => {
  let timeout;

  item.addEventListener('mouseenter', () => {
    clearTimeout(timeout);
    item.querySelector('.megamenu').style.display = 'block';
  });

  item.addEventListener('mouseleave', () => {
    timeout = setTimeout(() => {
      item.querySelector('.megamenu').style.display = 'none';
    }, 200); // Adjust the delay time if needed
  });
});

// Header Megamenu Function
window.addEventListener('scroll', () => {
  const announcementBar = document.querySelector('.announcements-bar')?.parentElement;
  const header = document.querySelector('.header-content');
  const megamenus = document.querySelectorAll('.megamenu');

  if (header && megamenus.length > 0 && announcementBar) {
    const headerHeight = header.offsetHeight - 1;

    megamenus.forEach(menu => {
      if (announcementBar.classList.contains('section-in-view')) {
        menu.style.top = 'unset';
      } else {
        menu.style.top = `${headerHeight}px`;
      }
    });
  }
});

// Avoid re-attaching listeners to the same element
function initSlider(cardSlider) {
  if (cardSlider.classList.contains('slider-initialized')) return;
  cardSlider.classList.add('slider-initialized');

  let cardPrev = cardSlider.querySelector('.gridy-arrows button[data-direction="prev"]');
  let cardNext = cardSlider.querySelector('.gridy-arrows button[data-direction="next"]');
  let cardGridy = cardSlider.querySelector('.gridy-track');

  if (!cardPrev || !cardNext || !cardGridy) return;

  cardPrev.style.visibility = 'hidden';

  const updateArrows = () => hidingArrows(cardPrev, cardNext, cardGridy);

  cardPrev.addEventListener('click', updateArrows);
  cardNext.addEventListener('click', updateArrows);
  cardGridy.addEventListener('scroll', updateArrows);
}

// Initial run for already-existing sliders
document.querySelectorAll('.card--product gridy-slider').forEach(initSlider);

// Watch for new sliders added later
const observer = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (!(node instanceof HTMLElement)) return;

      if (node.matches('.card--product gridy-slider')) {
        initSlider(node);
      } else {
        node.querySelectorAll('.card--product gridy-slider').forEach(initSlider);
      }
    });
  });
});

observer.observe(document.body, { childList: true, subtree: true });

function hidingArrows(cardPrev, cardNext, cardGridy) {
  let cardSliderFirst = cardGridy.firstElementChild;
  let cardSliderLast = cardGridy.lastElementChild;
  if (cardSliderFirst?.classList.contains('active')) {
    cardPrev.style.visibility = 'hidden';
  } else {
    cardPrev.style.visibility = 'visible';
  }
  if (cardSliderLast?.classList.contains('active')) {
    cardNext.style.visibility = 'hidden';
  } else {
    cardNext.style.visibility = 'visible';
  }
}

function goToNext() {
  const fcpSlider = document.querySelector('.fcp--slider-main');
  const fcpSlides = fcpSlider.querySelectorAll('.card');
  let currentIndex = 0;
  if (currentIndex < fcpSlides.length - 1) {
    currentIndex++;
    const slide = fcpSlides[currentIndex];
    if (slide) {
      slide.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  }
}

function goToPrev() {
  const fcpSlider = document.querySelector('.fcp--slider-main');
  const fcpSlides = fcpSlider.querySelectorAll('.card');
  let currentIndex = 0;
  if (currentIndex > 0) {
    currentIndex--;
    const slide = fcpSlides[currentIndex];
    if (slide) {
      slide.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  }
}

document.addEventListener('DOMContentLoaded', function () {
  let currentIndex = 0;
  let fcpSlides;

  // Wait until slides are available
  const waitForSlides = setInterval(() => {
    const fcpSlider = document.querySelector('.fcp--slider-main');
    if (fcpSlider) {
      fcpSlides = fcpSlider.querySelectorAll('.card');
      if (fcpSlides.length > 0) {
        clearInterval(waitForSlides); // Stop checking once found

        // Attach functions only after slides are available
        window.goToNext = function () {
          if (currentIndex < fcpSlides.length - 1) {
            currentIndex++;
            scrollToSlide(currentIndex);
          }
        };

        window.goToPrev = function () {
          if (currentIndex > 0) {
            currentIndex--;
            scrollToSlide(currentIndex);
          }
        };

        function scrollToSlide(index) {
          const slide = fcpSlides[currentIndex];
          if (slide) {
            slide.scrollIntoView({ behavior: 'smooth', inline: 'start' });
          }
        }
      }
    }
  }, 100); // Check every 100ms
});

// Cart Drawer Checkout Functionality
/**
 * Enables/disables the checkout button in the cart drawer based on the terms checkbox.
 * Side effect: Mutates DOM to disable/enable checkout button.
 */
function initCheckboxToggle() {
  const checkbox = document.getElementById('term-checkbox');
  const checkoutBtn = document.getElementById('checkout-btn');
  const validationMessage = document.getElementById('terms-validation-message');

  if (checkbox && checkoutBtn) {
    // Set initial disabled state
    checkoutBtn.disabled = false;
    if (!checkbox.checked) {
      checkoutBtn.classList.add('checkout-btn-disabled');
    }
    
    checkbox.addEventListener('change', function () {
      console.log('checkbox changed', this.checked);
      if (this.checked) {
        checkoutBtn.classList.remove('checkout-btn-disabled');
        if (validationMessage) {
          validationMessage.classList.remove('show');
        }
      } else {
        checkoutBtn.classList.add('checkout-btn-disabled');
      }
    });
    
    checkoutBtn.addEventListener('click', function (e) {
      console.log('checkoutBtn clicked, checkbox checked:', checkbox.checked);
      if (checkbox && !checkbox.checked) {
        e.preventDefault();
        e.stopPropagation();
        console.log('Preventing checkout, showing validation message');
        if (validationMessage) {
          validationMessage.classList.add('show');
          setTimeout(() => {
            validationMessage.classList.remove('show');
          }, 4000);
        }
        return false;
      }
    });
  } else if (checkoutBtn && !checkbox) {
    checkoutBtn.disabled = false;
    checkoutBtn.classList.remove('checkout-btn-disabled');
  }
}

initCheckboxToggle();

const cartDrawer = document.querySelector('.form-elements');

if (cartDrawer) {
  const observer = new MutationObserver(() => {
    initCheckboxToggle();
  });
  observer.observe(cartDrawer, { childList: true, subtree: true });
}

//Closing toggle for filters
document.addEventListener('DOMContentLoaded', function () {
  const detailElements = document.querySelectorAll('.filter-form details');

  detailElements.forEach(details => {
    details.addEventListener('toggle', function () {
      if (details.open) {
        detailElements.forEach(otherDetails => {
          if (otherDetails !== details) {
            otherDetails.removeAttribute('open');
          }
        });
      }
    });
  });
});

// ---------------
// Quick Add to Cart Custom Element
// ---------------
if (!customElements.get('quick-add-to-cart')) {
  customElements.define(
    'quick-add-to-cart',
    class QuickAddToCart extends HTMLElement {
      constructor() {
        super();
        this.variantId = this.getAttribute('data-variant-id');
        this.button = this;
        this.loading = false;
        this.handleClick = this.handleClick.bind(this);
      }

      /**
       * Sets up button role, tabIndex, and event listeners for accessibility and interaction.
       */
      connectedCallback() {
        if (!this.button.hasAttribute('role')) {
          this.button.setAttribute('role', 'button');
        }
        this.button.tabIndex = 0;
        this.button.addEventListener('click', this.handleClick);
        this.button.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleClick();
          }
        });
      }

      /**
       * Cleans up event listeners when element is removed from the DOM.
       */
      disconnectedCallback() {
        this.button.removeEventListener('click', this.handleClick);
      }

      /**
       * Updates the button UI state to reflect loading or ready status.
       * @param {boolean} isLoading
       */
      setLoading(isLoading) {
        this.loading = isLoading;
        if (isLoading) {
          this.button.classList.add('loading');
          this.button.setAttribute('aria-disabled', 'true');
          this.button.textContent = t('cart.adding', 'Adding...');
        } else {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-disabled');
          this.button.textContent = t('cart.add_to_cart', 'Add to cart');
        }
      }

      /**
       * Handles the quick add to cart button click event.
       * Sends an AJAX request to add the product variant to the cart and updates UI accordingly.
       * Side effects: Mutates button state, dispatches 'ajaxProduct:added' event, updates cart drawer.
       */
      async handleClick() {
        if (this.loading) return;
        if (!this.variantId) return;
        this.setLoading(true);
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ id: this.variantId, quantity: 1 }),
          });
          const json = await res.json();
          if (!res.ok) throw new Error(json.description || 'Error adding to cart');
          // Dispatch event to update cart drawer
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          this.setLoading(false);
          // Optionally, show a success state
          this.button.textContent = t('cart.added', 'Added');
          setTimeout(() => {
            this.button.textContent = t('cart.add_to_cart', 'Add to cart');
          }, 1200);
        } catch (err) {
          this.setLoading(false);
          this.button.textContent = t('cart.error', 'Error');
          setTimeout(() => {
            this.button.textContent = t('cart.add_to_cart', 'Add to cart');
          }, 2000);
        }
      }
    }
  );
}

// ---------------
// tier-discounts Progress Bar Component
// ---------------
if (!customElements.get('tier-discounts-progress')) {
  customElements.define(
    'tier-discounts-progress',
    class TierDiscountsProgress extends HTMLElement {
      constructor() {
        super();
        this.currentAmount = 0;
        this.currency = this.getAttribute('data-currency') || 'DKK';
        this.initThresholds();
        this.translations = this.getTranslations();
      }

      getTranslations() {
        const translationsEl = this.parentElement.querySelector('[data-tier-discounts-translations]');
        if (translationsEl) {
          return JSON.parse(translationsEl.textContent);
        }
        return {
          subtitle_html: 'Spend <span class="amount-needed">[[amount]]</span> more to get [[discount]]% off',
          reached_html: 'Congratulations! You have reached [[discount]]% discount!',
          reached_and_next_tier_html: 'Congratulations! You have reached [[current_discount]]% discount. Spend only [[amount]] more to get [[next_discount]]% discount.'
        };
      }

      initThresholds() {
        this.thresholds = [
          {
            amount: parseInt(this.getAttribute('data-threshold-1')) || 149900,
            discount: parseInt(this.getAttribute('data-discount-1')) || 10
          },
          {
            amount: parseInt(this.getAttribute('data-threshold-2')) || 299900,
            discount: parseInt(this.getAttribute('data-discount-2')) || 20
          },
          {
            amount: parseInt(this.getAttribute('data-threshold-3')) || 449900,
            discount: parseInt(this.getAttribute('data-discount-3')) || 25
          }
        ];
      }

      connectedCallback() {
        this.addEventListener('cart:update', this.handleCartUpdate.bind(this));
        document.addEventListener('ajaxProduct:added', this.handleProductAdded.bind(this));
        document.addEventListener('cart:calculators:update', this.handleCartUpdate.bind(this));
        this.updateFromCart();
      }

      disconnectedCallback() {
        this.removeEventListener('cart:update', this.handleCartUpdate.bind(this));
        document.removeEventListener('ajaxProduct:added', this.handleProductAdded.bind(this));
        document.removeEventListener('cart:calculators:update', this.handleCartUpdate.bind(this));
      }

      async updateFromCart() {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          
          // Use original_total_price (before discounts) instead of total_price (after discounts)
          this.currentAmount = cart.original_total_price || cart.total_price;
          
          console.log("Cart data:", {
            total_price: cart.total_price,
            original_total_price: cart.original_total_price,
            using_amount: this.currentAmount
          });
          
          this.updateProgress();
        } catch (error) {
          console.error('Error fetching cart:', error);
        }
      }

      handleCartUpdate(event) {
        if (event.detail && event.detail.original_total_price !== undefined) {
          // Use original_total_price from event detail if available
          this.currentAmount = event.detail.original_total_price;
        } else if (event.detail && event.detail.total_price !== undefined) {
          // Fallback to total_price if original_total_price not available
          this.currentAmount = event.detail.total_price;
        } else {
          this.updateFromCart();
          return;
        }
        
        console.log("Event update - using amount:", this.currentAmount);
        this.updateProgress();
      }

      handleProductAdded() {
        setTimeout(() => {
          this.updateFromCart();
        }, 100);
      }

      getCurrentTier(amount) {
        // Get tier settings from data attributes
        const threshold1 = parseInt(this.dataset['threshold-1']) || 149900;
        const discount1 = parseInt(this.dataset['discount-1']) || 10;
        const threshold2 = parseInt(this.dataset['threshold-2']) || 149900;
        const discount2 = parseInt(this.dataset['discount-2']) || 20;
        const threshold3 = parseInt(this.dataset['threshold-3']) || 299900;
        const discount3 = parseInt(this.dataset['discount-3']) || 25;

        // Show what discount they're working toward next
        if (amount >= threshold3) { // Highest tier reached
          return {
            current: { amount: threshold3, discount: discount3 },
            next: null,
            tierIndex: 2
          };
        } else if (amount >= threshold2) { // Middle tier reached, working toward highest
          return {
            current: { amount: threshold2, discount: discount2 },
            next: { amount: threshold3, discount: discount3 },
            tierIndex: 1
          };
        } else { // Lowest tier, working toward middle
          return {
            current: { amount: 0, discount: discount1 },
            next: { amount: threshold2, discount: discount2 },
            tierIndex: 0
          };
        }
      }

      updateProgress() {
        const progressElement = this.querySelector('.tier-discounts-progress');
        const subtitleElement = this.querySelector('.tier-discounts-subtitle');
        const scaleElement = this.querySelector('.tier-discounts-scale');
        
        if (!progressElement || !subtitleElement) return;

        const shopCurrency = window?.Shopify?.currency?.active || this.currency;
        let currentAmountInBase = this.currentAmount;

        if (shopCurrency !== this.currency && window?.Shopify?.currency?.rate) {
          // Convert current amount to base currency (DKK) for tier calculation
          currentAmountInBase = this.currentAmount / window.Shopify.currency.rate;
        }

        const tierInfo = this.getCurrentTier(currentAmountInBase);
        
        // Always use highest tier from settings as target for percentage calculation
        const HIGHEST_TIER_AMOUNT = parseInt(this.getAttribute('data-threshold-3')) || 449900;
        let targetAmountForPercentage = HIGHEST_TIER_AMOUNT;
        
        // Calculate percentage using same currency amounts
        let percentage = Math.min((currentAmountInBase / targetAmountForPercentage) * 100, 100);
        
        console.log('🚀 Tier Discounts Debug:', {
          currentAmount: this.currentAmount,
          currentAmountInBase: currentAmountInBase,
          currentTier: tierInfo.current?.discount || 'none',
          nextTier: tierInfo.next?.discount || 'max reached',
          highestTierAmount: HIGHEST_TIER_AMOUNT,
          targetForPercentage: targetAmountForPercentage,
          calculatedPercentage: percentage.toFixed(1) + '%',
          shopCurrency,
          displayCurrency: this.currency,
          currencyRate: window?.Shopify?.currency?.rate
        });
        
        let message = '';

        if (!this.translations) {
          console.error('Translations not initialized.');
          return;
        }

        if (tierInfo.current && !tierInfo.next) {
          // Maximum tier reached
          percentage = 100;
          message = this.translations.reached_html.replace('[[discount]]', tierInfo.current.discount);
          progressElement.classList.add('completed');
          progressElement.classList.remove('in-progress');
        } else if (tierInfo.next) {
          // Show progress to next tier
          let nextTierAmount = tierInfo.next.amount;
          
          // Handle currency conversion for display amount only
          if (shopCurrency !== this.currency && window?.Shopify?.currency?.rate) {
            nextTierAmount = nextTierAmount * window.Shopify.currency.rate;
          }
          
          let amountNeeded = nextTierAmount - this.currentAmount;
          
          if (amountNeeded > 0) {
            message = this.translations.reached_and_next_tier_html
              .replace('[[current_discount]]', tierInfo.current.discount)
              .replace('[[amount]]', this.formatMoney(amountNeeded))
              .replace('[[next_discount]]', tierInfo.next.discount);
            progressElement.classList.remove('completed');
            progressElement.classList.add('in-progress');
          } else {
            message = this.translations.reached_html.replace('[[discount]]', tierInfo.next.discount);
            progressElement.classList.add('completed');
            progressElement.classList.remove('in-progress');
          }
        }

        progressElement.style.width = `${percentage}%`;
        console.log({percentage});
        console.log(progressElement.style.width);
        
        subtitleElement.innerHTML = message;

        // Update scale to show discount percentages with achieved states
        if (scaleElement) {
          const tier1Class = tierInfo.tierIndex >= 0 ? 'achieved' : '';
          const tier2Class = tierInfo.tierIndex >= 1 ? 'achieved' : '';
          const tier3Class = tierInfo.tierIndex >= 2 ? 'achieved' : '';
          
          const discount1 = parseInt(this.getAttribute('data-discount-1')) || 10;
          const discount2 = parseInt(this.getAttribute('data-discount-2')) || 20;
          const discount3 = parseInt(this.getAttribute('data-discount-3')) || 25;
          
          scaleElement.innerHTML = `
            <span class="tier-1 ${tier1Class}">${discount1}%</span>
            <span class="tier-2 ${tier2Class}">${discount2}%</span>
            <span class="tier-3 ${tier3Class}">${discount3}%</span>
          `;
        }
      }

      formatMoney(amount) {
        // Check if Woolman utils is available
        if (typeof Woolman !== 'undefined' && 
            typeof Woolman.Utils !== 'undefined' && 
            typeof Woolman.Utils.formatMoney === 'function') {
          try {
            const moneyFormat = (typeof theme !== 'undefined' && theme.settings && theme.settings.moneyFormat) 
              ? theme.settings.moneyFormat 
              : '{{amount_with_comma_separator}} kr.';
            return Woolman.Utils.formatMoney(amount, moneyFormat);
          } catch (error) {
            console.warn('Error formatting money with Woolman.Utils:', error);
          }
        }
        
        // Fallback formatting
        const formatted = (amount / 100).toFixed(2).replace('.', ',');
        return `${formatted} kr.`;
      }
    }
  );
}



// ---------------
// Prevent email scraping
// ---------------
document.addEventListener("DOMContentLoaded", function () {
  const links = document.querySelectorAll('a[href^="mailto:"]');

  links.forEach(link => {
    const email = link.getAttribute("href").replace("mailto:", "");

    // Basic obfuscation
    const [user, domain] = email.split("@");

    // Clear the email to bots before JS finishes
    link.setAttribute("href", "#");
    link.textContent = "[email protected]";

    // Reapply real email with JS (only visible to users)
    setTimeout(() => {
      const safeEmail = `${user}@${domain}`;
      link.setAttribute("href", `mailto:${safeEmail}`);
      link.textContent = safeEmail;
    }, 50); // Small delay helps avoid some bots scraping before JS runs
  });
});

function isHeadless() {
  return (
    navigator.webdriver ||                          // true in headless
    /HeadlessChrome/.test(navigator.userAgent) ||   // common userAgent
    !navigator.plugins.length ||                    // no plugins
    !navigator.languages ||                         // missing lang info
    screen.width === 0 || screen.height === 0       // virtual screens
  );
}

if (isHeadless()) {
  // Option 1: redirect
  // window.location.href = "/blocked";
  
  // Option 2: disable email links
  document.querySelectorAll('a[href^="mailto:"]').forEach(el => {
    el.removeAttribute("href");
    el.textContent = "Blocked for bots";
  });
}
